/**
 * Exceptions Tab — Admin Client Detail
 * View, filter, and act on AutoLedgerException records for a client.
 * Actions: Resolve (Open/Reopened), Suppress (Open/Reopened), Reopen (Resolved/Suppressed).
 */
import { useCallback, useEffect, useState } from "react";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Select, type SelectItemType } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { useAdminApi } from "@/services/api/admin-api";
import type { ExceptionAdminDetailDto, ExceptionAdminListItemDto, ResolveExceptionRequestDto, SuppressExceptionRequestDto } from "@/types/api/admin";

// =============================================================================
// TYPES & CONSTANTS
// =============================================================================

interface ExceptionsTabProps {
    clientId: number;
}

const PAGE_SIZE = 25;

const STATUS_OPTIONS: SelectItemType[] = [
    { id: "", label: "All statuses" },
    { id: "Open", label: "Open" },
    { id: "Resolved", label: "Resolved" },
    { id: "Suppressed", label: "Suppressed" },
    { id: "Reopened", label: "Reopened" },
];

const SEVERITY_OPTIONS: SelectItemType[] = [
    { id: "", label: "All severities" },
    { id: "Warning", label: "Warning" },
    { id: "Blocking", label: "Blocking" },
];

const SOURCE_OPTIONS: SelectItemType[] = [
    { id: "", label: "All sources" },
    { id: "mailbox", label: "Mailbox Worker" },
    { id: "bridge", label: "Bridge" },
    { id: "frontend", label: "Frontend" },
];

const RESOLUTION_TYPE_OPTIONS: SelectItemType[] = [
    { id: "Corrected", label: "Corrected" },
    { id: "Accepted", label: "Accepted" },
    { id: "Overridden", label: "Overridden" },
];

// =============================================================================
// HELPERS
// =============================================================================

function severityColor(severity: string): "warning" | "error" | "gray" {
    if (severity === "Blocking") return "error";
    if (severity === "Warning") return "warning";
    return "gray";
}

function formatEvidence(evidence: string): string {
    try {
        return JSON.stringify(JSON.parse(evidence), null, 2);
    } catch {
        return evidence;
    }
}

function statusColor(status: string): "success" | "warning" | "error" | "gray" | "brand" {
    switch (status) {
        case "Open":
            return "error";
        case "Resolved":
            return "success";
        case "Suppressed":
            return "gray";
        case "Reopened":
            return "warning";
        default:
            return "gray";
    }
}

// =============================================================================
// EXPANDED ROW — detail + actions
// =============================================================================

interface ExpandedRowProps {
    clientId: number;
    exception: ExceptionAdminListItemDto;
    onActionComplete: () => void;
}

function ExpandedRow({ clientId, exception, onActionComplete }: ExpandedRowProps) {
    const adminApi = useAdminApi();
    const [detail, setDetail] = useState<ExceptionAdminDetailDto | null>(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(true);
    const [detailError, setDetailError] = useState<string | null>(null);

    // Resolve form state
    const [resolveType, setResolveType] = useState<string>("Corrected");
    const [resolveNote, setResolveNote] = useState("");

    // Suppress form state
    const [suppressNote, setSuppressNote] = useState("");

    const [actionError, setActionError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        async function fetchDetail() {
            setIsLoadingDetail(true);
            setDetailError(null);
            try {
                const d = await adminApi.getClientException(clientId, exception.id);
                if (!cancelled) setDetail(d);
            } catch (err) {
                if (!cancelled) setDetailError(err instanceof Error ? err.message : "Failed to load details.");
            } finally {
                if (!cancelled) setIsLoadingDetail(false);
            }
        }
        fetchDetail();
        return () => {
            cancelled = true;
        };
    }, [adminApi, clientId, exception.id]);

    const handleResolve = useCallback(async () => {
        setIsSaving(true);
        setActionError(null);
        try {
            const dto: ResolveExceptionRequestDto = {
                resolutionType: resolveType,
                resolutionNote: resolveNote || undefined,
            };
            await adminApi.resolveClientException(clientId, exception.id, dto);
            onActionComplete();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : "Failed to resolve exception.");
        } finally {
            setIsSaving(false);
        }
    }, [adminApi, clientId, exception.id, resolveType, resolveNote, onActionComplete]);

    const handleSuppress = useCallback(async () => {
        if (!suppressNote.trim()) {
            setActionError("A note is required to suppress an exception.");
            return;
        }
        setIsSaving(true);
        setActionError(null);
        try {
            const dto: SuppressExceptionRequestDto = { resolutionNote: suppressNote };
            await adminApi.suppressClientException(clientId, exception.id, dto);
            onActionComplete();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : "Failed to suppress exception.");
        } finally {
            setIsSaving(false);
        }
    }, [adminApi, clientId, exception.id, suppressNote, onActionComplete]);

    const handleReopen = useCallback(async () => {
        setIsSaving(true);
        setActionError(null);
        try {
            await adminApi.reopenClientException(clientId, exception.id);
            onActionComplete();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : "Failed to reopen exception.");
        } finally {
            setIsSaving(false);
        }
    }, [adminApi, clientId, exception.id, onActionComplete]);

    const canResolveOrSuppress = exception.status === "Open" || exception.status === "Reopened";
    const canReopen = exception.status === "Resolved" || exception.status === "Suppressed";

    return (
        <div className="border-brand-secondary bg-primary_subtle space-y-4 rounded-lg border px-4 py-4">
            {/* Detail values */}
            {isLoadingDetail ? (
                <LoadingIndicator size="sm" label="Loading details..." />
            ) : detailError ? (
                <p className="text-sm text-error-primary">{detailError}</p>
            ) : (
                <div className="space-y-3 text-sm">
                    {/* Message — prominent, full width */}
                    {exception.message && (
                        <div>
                            <p className="mb-1 text-xs font-medium text-secondary">Message</p>
                            <p className="text-primary">{exception.message}</p>
                        </div>
                    )}

                    {/* Classification & identity */}
                    <div className="grid grid-cols-2 gap-x-8 gap-y-2 sm:grid-cols-3">
                        <div>
                            <p className="text-xs font-medium text-secondary">Code</p>
                            <span className="rounded bg-secondary px-1.5 py-0.5 font-mono text-xs text-secondary">{exception.code}</span>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-secondary">Severity</p>
                            <Badge color={severityColor(exception.severity)} size="sm" type="pill-color">
                                {exception.severity}
                            </Badge>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-secondary">Category</p>
                            <p className="text-primary">{exception.category}</p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-secondary">Status</p>
                            <Badge color={statusColor(exception.status)} size="sm" type="pill-color">
                                {exception.status}
                            </Badge>
                        </div>
                        {exception.field && (
                            <div>
                                <p className="text-xs font-medium text-secondary">Field</p>
                                <p className="text-primary">{exception.field}</p>
                            </div>
                        )}
                        <div>
                            <p className="text-xs font-medium text-secondary">Entity</p>
                            <p className="text-primary">
                                {exception.entityType && exception.entityId != null ? (
                                    <>
                                        {exception.entityType} #{exception.entityId}
                                        {exception.entityName && <span className="ml-1 text-tertiary">({exception.entityName})</span>}
                                    </>
                                ) : (
                                    "—"
                                )}
                            </p>
                        </div>
                        {exception.organizationId && (
                            <div>
                                <p className="text-xs font-medium text-secondary">Organization ID</p>
                                <p className="text-primary">{exception.organizationId}</p>
                            </div>
                        )}
                        {exception.ruleId && (
                            <div>
                                <p className="text-xs font-medium text-secondary">Rule ID</p>
                                <p className="text-primary">{exception.ruleId}</p>
                            </div>
                        )}
                        {exception.extractionVersionId && (
                            <div>
                                <p className="text-xs font-medium text-secondary">Extraction Version ID</p>
                                <p className="text-primary">{exception.extractionVersionId}</p>
                            </div>
                        )}
                    </div>

                    {/* Expected / Actual */}
                    {(exception.expectedValue || exception.actualValue) && (
                        <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                            {exception.expectedValue && (
                                <div>
                                    <p className="text-xs font-medium text-secondary">Expected</p>
                                    <p className="text-primary">{exception.expectedValue}</p>
                                </div>
                            )}
                            {exception.actualValue && (
                                <div>
                                    <p className="text-xs font-medium text-secondary">Actual</p>
                                    <p className="text-primary">{exception.actualValue}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Resolution info */}
                    {(exception.resolutionType || exception.resolvedAt || exception.resolvedBy || exception.resolutionNote) && (
                        <div className="border-t border-secondary pt-2">
                            <p className="mb-2 text-xs font-semibold tracking-wide text-secondary uppercase">Resolution</p>
                            <div className="grid grid-cols-2 gap-x-8 gap-y-2 sm:grid-cols-3">
                                {exception.resolutionType && (
                                    <div>
                                        <p className="text-xs font-medium text-secondary">Type</p>
                                        <p className="text-primary">{exception.resolutionType}</p>
                                    </div>
                                )}
                                {exception.resolvedBy && (
                                    <div>
                                        <p className="text-xs font-medium text-secondary">Resolved By</p>
                                        <p className="text-primary">User #{exception.resolvedBy}</p>
                                    </div>
                                )}
                                {exception.resolvedAt && (
                                    <div>
                                        <p className="text-xs font-medium text-secondary">Resolved At</p>
                                        <p className="text-primary">{new Date(exception.resolvedAt).toLocaleString()}</p>
                                    </div>
                                )}
                            </div>
                            {exception.resolutionNote && (
                                <div className="mt-2">
                                    <p className="text-xs font-medium text-secondary">Note</p>
                                    <p className="text-primary">{exception.resolutionNote}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Timestamps */}
                    <div className="grid grid-cols-2 gap-x-8 gap-y-2 sm:grid-cols-3">
                        <div>
                            <p className="text-xs font-medium text-secondary">Created</p>
                            <p className="text-tertiary">{new Date(exception.createdAt).toLocaleString()}</p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-secondary">Updated</p>
                            <p className="text-tertiary">{new Date(exception.updatedAt).toLocaleString()}</p>
                        </div>
                    </div>

                    {/* Evidence JSON */}
                    {detail?.evidence && (
                        <div className="mt-2">
                            <p className="mb-1 text-xs font-medium text-secondary">Evidence</p>
                            <pre className="max-h-80 overflow-y-auto rounded bg-secondary p-3 text-xs break-all whitespace-pre-wrap text-primary">
                                {formatEvidence(detail.evidence)}
                            </pre>
                        </div>
                    )}
                </div>
            )}

            {/* Actions */}
            <div className="border-t border-secondary pt-3">
                {canResolveOrSuppress && (
                    <div className="space-y-4">
                        {/* Resolve */}
                        <div className="space-y-2">
                            <p className="text-xs font-semibold tracking-wide text-secondary uppercase">Resolve</p>
                            <div className="flex items-end gap-3">
                                <div className="w-44">
                                    <Select
                                        label="Resolution type"
                                        items={RESOLUTION_TYPE_OPTIONS}
                                        selectedKey={resolveType}
                                        onSelectionChange={(key) => setResolveType(String(key ?? "Corrected"))}
                                        size="sm"
                                        isDisabled={isSaving}
                                    >
                                        {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                                    </Select>
                                </div>
                                <div className="flex-1">
                                    <label className="mb-1 block text-xs font-medium text-secondary">Note (optional)</label>
                                    <input
                                        type="text"
                                        value={resolveNote}
                                        onChange={(e) => setResolveNote(e.target.value)}
                                        placeholder="Resolution note..."
                                        disabled={isSaving}
                                        className="w-full rounded-md border border-secondary bg-primary px-3 py-1.5 text-sm text-primary placeholder-quaternary focus:border-brand focus:outline-none disabled:opacity-50"
                                    />
                                </div>
                                <Button color="primary" size="sm" onClick={handleResolve} isDisabled={isSaving}>
                                    Resolve
                                </Button>
                            </div>
                        </div>

                        {/* Suppress */}
                        <div className="space-y-2">
                            <p className="text-xs font-semibold tracking-wide text-secondary uppercase">Suppress</p>
                            <div className="flex items-end gap-3">
                                <div className="flex-1">
                                    <label className="mb-1 block text-xs font-medium text-secondary">Note (required)</label>
                                    <input
                                        type="text"
                                        value={suppressNote}
                                        onChange={(e) => setSuppressNote(e.target.value)}
                                        placeholder="Reason for suppression..."
                                        disabled={isSaving}
                                        className="w-full rounded-md border border-secondary bg-primary px-3 py-1.5 text-sm text-primary placeholder-quaternary focus:border-brand focus:outline-none disabled:opacity-50"
                                    />
                                </div>
                                <Button color="secondary" size="sm" onClick={handleSuppress} isDisabled={isSaving || !suppressNote.trim()}>
                                    Suppress
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {canReopen && (
                    <div className="flex items-center gap-3">
                        <p className="text-sm text-tertiary">Reopen this exception to mark it as requiring attention again.</p>
                        <Button color="secondary" size="sm" onClick={handleReopen} isDisabled={isSaving}>
                            Reopen
                        </Button>
                    </div>
                )}
            </div>

            {actionError && <p className="text-sm text-error-primary">{actionError}</p>}
            {isSaving && <LoadingIndicator size="sm" />}
        </div>
    );
}

// =============================================================================
// EXCEPTIONS TAB
// =============================================================================

export function ExceptionsTab({ clientId }: ExceptionsTabProps) {
    const adminApi = useAdminApi();
    const [exceptions, setExceptions] = useState<ExceptionAdminListItemDto[]>([]);
    const [total, setTotal] = useState(0);
    const [skip, setSkip] = useState(0);
    const [statusFilter, setStatusFilter] = useState("");
    const [severityFilter, setSeverityFilter] = useState("");
    const [sourceFilter, setSourceFilter] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedId, setExpandedId] = useState<number | null>(null);

    // The table can be wider than its scroll container; the expanded detail row is
    // pinned (sticky) to the visible width so it never requires horizontal scrolling.
    const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
    const [panelWidth, setPanelWidth] = useState<number | null>(null);

    useEffect(() => {
        if (!scrollEl) return;
        const update = () => setPanelWidth(scrollEl.clientWidth);
        update();
        const observer = new ResizeObserver(update);
        observer.observe(scrollEl);
        return () => observer.disconnect();
    }, [scrollEl]);

    const fetchExceptions = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await adminApi.listClientExceptions(clientId, {
                status: statusFilter || undefined,
                severity: severityFilter || undefined,
                source: sourceFilter || undefined,
                pageSize: PAGE_SIZE,
                skip,
            });
            setExceptions(res.data);
            setTotal(res.total);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load exceptions.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi, clientId, statusFilter, severityFilter, sourceFilter, skip]);

    useEffect(() => {
        fetchExceptions();
    }, [fetchExceptions]);

    // Reset skip when filters change
    const handleStatusChange = useCallback((key: string | number | null) => {
        setStatusFilter(String(key ?? ""));
        setSkip(0);
    }, []);

    const handleSeverityChange = useCallback((key: string | number | null) => {
        setSeverityFilter(String(key ?? ""));
        setSkip(0);
    }, []);

    const handleSourceChange = useCallback((key: string | number | null) => {
        setSourceFilter(String(key ?? ""));
        setSkip(0);
    }, []);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

    return (
        <div className="space-y-6">
            <section>
                <h3 className="text-lg font-semibold text-primary">Exceptions</h3>
                <p className="mt-1 text-sm text-tertiary">View and manage validation exceptions for this client.</p>
            </section>

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
                <div className="w-44">
                    <Select label="Status" items={STATUS_OPTIONS} selectedKey={statusFilter} onSelectionChange={handleStatusChange} size="sm">
                        {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                    </Select>
                </div>
                <div className="w-44">
                    <Select label="Severity" items={SEVERITY_OPTIONS} selectedKey={severityFilter} onSelectionChange={handleSeverityChange} size="sm">
                        {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                    </Select>
                </div>
                <div className="w-44">
                    <Select label="Source" items={SOURCE_OPTIONS} selectedKey={sourceFilter} onSelectionChange={handleSourceChange} size="sm">
                        {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                    </Select>
                </div>
            </div>

            {/* Table */}
            {isLoading ? (
                <div className="py-8">
                    <LoadingIndicator size="sm" label="Loading exceptions..." />
                </div>
            ) : error ? (
                <p className="text-sm text-error-primary">{error}</p>
            ) : exceptions.length === 0 ? (
                <p className="py-8 text-center text-sm text-quaternary">No exceptions found.</p>
            ) : (
                <>
                    <div ref={setScrollEl} className="overflow-x-auto rounded-lg border border-secondary">
                        <table className="w-full text-left text-sm">
                            <thead className="border-b border-secondary bg-secondary_subtle">
                                <tr>
                                    <th className="px-4 py-3 font-medium text-secondary">ID</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Code</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Severity</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Category</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Field</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Message</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Status</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Entity</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Created</th>
                                    <th className="w-24 px-4 py-3 font-medium text-secondary"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-secondary">
                                {exceptions.map((exc) => {
                                    const isExpanded = expandedId === exc.id;
                                    const entityLabel = exc.entityName
                                        ? exc.entityName
                                        : exc.entityType && exc.entityId != null
                                          ? `${exc.entityType} #${exc.entityId}`
                                          : "—";
                                    const entityTitle = exc.entityName
                                        ? `${exc.entityType ?? ""} #${exc.entityId ?? ""}: ${exc.entityName}`.trim()
                                        : entityLabel;
                                    return (
                                        <>
                                            <tr key={exc.id} className="group hover:bg-primary_hover">
                                                <td className="px-4 py-3 text-tertiary">{exc.id}</td>
                                                <td className="px-4 py-3">
                                                    <span className="rounded bg-secondary px-1.5 py-0.5 font-mono text-xs text-secondary">{exc.code}</span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <Badge color={severityColor(exc.severity)} size="sm" type="pill-color">
                                                        {exc.severity}
                                                    </Badge>
                                                </td>
                                                <td className="px-4 py-3 text-tertiary">{exc.category}</td>
                                                <td className="px-4 py-3 text-tertiary">{exc.field ?? "—"}</td>
                                                <td className="max-w-xs truncate px-4 py-3 text-tertiary" title={exc.message}>
                                                    {exc.message || "—"}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <Badge color={statusColor(exc.status)} size="sm" type="pill-color">
                                                        {exc.status}
                                                    </Badge>
                                                </td>
                                                <td className="max-w-xs truncate px-4 py-3 text-tertiary" title={entityTitle}>
                                                    {entityLabel}
                                                </td>
                                                <td className="px-4 py-3 text-tertiary">{new Date(exc.createdAt).toLocaleDateString()}</td>
                                                <td className="w-24 px-4 py-3">
                                                    <Button
                                                        color={isExpanded ? "primary" : "secondary"}
                                                        size="sm"
                                                        onClick={() => setExpandedId(isExpanded ? null : exc.id)}
                                                    >
                                                        {isExpanded ? "Close" : "Details"}
                                                    </Button>
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <tr key={`${exc.id}-detail`}>
                                                    <td colSpan={10} className="pt-0 pb-4">
                                                        <div className="sticky left-0 px-4" style={panelWidth ? { width: panelWidth } : undefined}>
                                                            <ExpandedRow
                                                                clientId={clientId}
                                                                exception={exc}
                                                                onActionComplete={() => {
                                                                    setExpandedId(null);
                                                                    fetchExceptions();
                                                                }}
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {total > PAGE_SIZE && (
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-tertiary">
                                Showing {skip + 1}–{Math.min(skip + PAGE_SIZE, total)} of {total}
                            </p>
                            <div className="flex gap-2">
                                <Button color="secondary" size="sm" isDisabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}>
                                    Previous
                                </Button>
                                <Button color="secondary" size="sm" isDisabled={currentPage >= totalPages} onClick={() => setSkip(skip + PAGE_SIZE)}>
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
